if (!customElements.get('tabbed-collection-lineup')) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  // Hover intent: switch only when the pointer rests on a tab, not while sweeping across
  const HOVER_DELAY = 150;

  class TclSlider {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('.tcl__track');
      this.controls = root.querySelector('.tcl__controls');
      this.prevButton = root.querySelector('[data-tcl-prev]');
      this.nextButton = root.querySelector('[data-tcl-next]');
      this.dotsWrap = root.querySelector('[data-tcl-dots]');
      this.slides = Array.from(this.track.children);
      this.dots = [];
      this.frame = null;

      this.onScroll = this.onScroll.bind(this);
      this.onPrev = () => this.go(-1);
      this.onNext = () => this.go(1);
      this.onDotClick = this.onDotClick.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.prevButton?.addEventListener('click', this.onPrev);
      this.nextButton?.addEventListener('click', this.onNext);
      this.dotsWrap?.addEventListener('click', this.onDotClick);

      // Fires when a hidden panel becomes visible (0 → real width) and on viewport resize
      this.resizeObserver = new ResizeObserver(() => this.onScroll());
      this.resizeObserver.observe(this.track);
    }

    get behavior() {
      return reducedMotion.matches ? 'auto' : 'smooth';
    }

    get maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    // Scroll position that brings a slide to its snap point (start on desktop, center on mobile)
    offsetOf(slide) {
      const trackRect = this.track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const align = getComputedStyle(slide).scrollSnapAlign;
      const paddingStart = parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
      let left = slideRect.left - trackRect.left + this.track.scrollLeft;
      left = align.includes('center') ? left - (this.track.clientWidth - slideRect.width) / 2 : left - paddingStart;
      return Math.min(Math.max(0, left), this.maxScroll);
    }

    get activeIndex() {
      const position = this.track.scrollLeft;
      if (position >= this.maxScroll - 2) return this.slides.length - 1;
      let best = 0;
      let bestDistance = Infinity;
      this.slides.forEach((slide, index) => {
        const distance = Math.abs(this.offsetOf(slide) - position);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });
      return best;
    }

    go(direction) {
      const index = Math.min(Math.max(this.activeIndex + direction, 0), this.slides.length - 1);
      this.scrollToSlide(index);
    }

    scrollToSlide(index) {
      const slide = this.slides[index];
      if (slide) this.track.scrollTo({ left: this.offsetOf(slide), behavior: this.behavior });
    }

    onDotClick(event) {
      const dot = event.target.closest('.tcl__dot');
      if (dot) this.scrollToSlide(Number(dot.dataset.index));
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    reset() {
      this.track.scrollTo({ left: 0, behavior: 'instant' });
      this.update();
    }

    update() {
      // Hidden panel: nothing to measure yet, ResizeObserver will call back on show
      if (this.track.clientWidth === 0) return;

      const position = this.track.scrollLeft;
      const max = this.maxScroll;
      const overflowing = max > 2;

      if (this.controls) this.controls.hidden = !overflowing;
      if (!overflowing) return;

      if (this.prevButton) this.prevButton.disabled = position <= 2;
      if (this.nextButton) this.nextButton.disabled = position >= max - 2;

      if (!this.dotsWrap) return;
      if (this.dots.length !== this.slides.length) this.renderDots();
      const active = this.activeIndex;
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', index === active ? 'true' : 'false'));
    }

    renderDots() {
      const label = this.dotsWrap.dataset.label || '[n]';
      this.dotsWrap.replaceChildren();
      this.dots = this.slides.map((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'tcl__dot';
        dot.dataset.index = index;
        dot.setAttribute('aria-label', label.replace('[n]', index + 1));
        this.dotsWrap.appendChild(dot);
        return dot;
      });
    }

    destroy() {
      this.track.removeEventListener('scroll', this.onScroll);
      this.prevButton?.removeEventListener('click', this.onPrev);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.dotsWrap?.removeEventListener('click', this.onDotClick);
      this.resizeObserver.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }
  }

  class TabbedCollectionLineup extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('.tcl__panel'));
      this.scroller = this.querySelector('.tcl__tabs-scroller');
      this.tablist = this.querySelector('[role="tablist"]');
      this.sliders = new Map();
      this.hoverTimer = null;

      this.panels.forEach((panel) => {
        const sliderRoot = panel.querySelector('[data-tcl-slider]');
        if (sliderRoot) this.sliders.set(panel, new TclSlider(sliderRoot));
      });

      this.onTabClick = this.onTabClick.bind(this);
      this.onTabKeydown = this.onTabKeydown.bind(this);
      this.onTabPointerOver = this.onTabPointerOver.bind(this);
      this.onTabPointerLeave = this.onTabPointerLeave.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onSectionUnload = this.onSectionUnload.bind(this);

      this.tablist?.addEventListener('click', this.onTabClick);
      this.tablist?.addEventListener('keydown', this.onTabKeydown);

      if (this.hasAttribute('data-hover-switch')) {
        this.tablist?.addEventListener('pointerover', this.onTabPointerOver);
        this.tablist?.addEventListener('pointerleave', this.onTabPointerLeave);
      }

      if (window.Shopify?.designMode) {
        this.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:section:unload', this.onSectionUnload);
      }

      this.setupIndicator();
      this.setupSticky();

      const activeIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      if (activeIndex > -1) this.scrollTabIntoView(this.tabs[activeIndex]);
    }

    // Mobile sticky tab bar (CSS does the sticking). JS keeps it right under the theme header,
    // which hides on scroll down and slides back on scroll up, and flags the stuck state.
    setupSticky() {
      if (!this.scroller) return;
      this.stickyQuery = window.matchMedia('(max-width: 749px)');
      this.headerSection = document.querySelector('.shopify-section-header');
      this.stickyTop = 0;
      this.inView = false;

      this.onWindowScroll = () => {
        if (this.stickyFrame) return;
        this.stickyFrame = requestAnimationFrame(() => {
          this.stickyFrame = null;
          this.updateSticky();
        });
      };

      this.viewObserver = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.onWindowScroll();
      });
      this.viewObserver.observe(this);

      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
      // Header reveal/hide animates after the last scroll event
      this.headerSection?.addEventListener('transitionend', this.onWindowScroll);
      this.headerSection?.addEventListener('animationend', this.onWindowScroll);
    }

    updateSticky() {
      if (!this.inView || !this.stickyQuery.matches) {
        this.scroller.classList.remove('is-stuck');
        return;
      }

      const headerBottom = this.headerSection ? this.headerSection.getBoundingClientRect().bottom : 0;
      const top = Math.max(0, Math.round(headerBottom));
      if (top !== this.stickyTop) {
        this.stickyTop = top;
        this.style.setProperty('--tcl-sticky-top', `${top}px`);
      }

      this.scroller.classList.toggle('is-stuck', this.scroller.getBoundingClientRect().top <= top + 1);
    }

    // Switching tabs while the bar is stuck: start the new tab from its top, not mid-content
    scrollToPanelTop(panel) {
      if (!this.stickyQuery?.matches || !this.scroller.classList.contains('is-stuck')) return;

      // Any page scroll hides the theme header on mobile, so align to the bar alone
      document.querySelector('sticky-header')?.dispatchEvent(new Event('preventHeaderReveal'));
      const target = panel.getBoundingClientRect().top + window.scrollY - this.scroller.offsetHeight;
      window.scrollTo({ top: target, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    // One pill that slides between tabs instead of each tab toggling its own background
    setupIndicator() {
      if (!this.tablist || this.tabs.length < 2) return;
      this.indicator =
        this.tablist.querySelector('.tcl__tab-indicator') || Object.assign(document.createElement('span'), { className: 'tcl__tab-indicator' });
      this.indicator.setAttribute('aria-hidden', 'true');
      this.tablist.prepend(this.indicator);
      this.tablist.classList.add('tcl__tabs--indicator');

      // Tab widths change with web fonts loading and viewport resizes
      this.indicatorObserver = new ResizeObserver(() => this.moveIndicator());
      this.tabs.forEach((tab) => this.indicatorObserver.observe(tab));
      this.moveIndicator();

      // Place it first, animate only later moves
      requestAnimationFrame(() => requestAnimationFrame(() => this.indicator?.classList.add('is-animated')));
    }

    moveIndicator() {
      const tab = this.tabs.find((item) => item.getAttribute('aria-selected') === 'true');
      if (!this.indicator || !tab || !tab.offsetWidth) return;
      this.indicator.style.width = `${tab.offsetWidth}px`;
      this.indicator.style.transform = `translateX(${tab.offsetLeft}px)`;
    }

    disconnectedCallback() {
      this.destroy();
    }

    destroy() {
      clearTimeout(this.hoverTimer);
      this.indicatorObserver?.disconnect();
      this.viewObserver?.disconnect();
      if (this.onWindowScroll) {
        window.removeEventListener('scroll', this.onWindowScroll);
        this.headerSection?.removeEventListener('transitionend', this.onWindowScroll);
        this.headerSection?.removeEventListener('animationend', this.onWindowScroll);
      }
      if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
      this.tablist?.removeEventListener('click', this.onTabClick);
      this.tablist?.removeEventListener('keydown', this.onTabKeydown);
      this.tablist?.removeEventListener('pointerover', this.onTabPointerOver);
      this.tablist?.removeEventListener('pointerleave', this.onTabPointerLeave);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      this.sliders?.forEach((slider) => slider.destroy());
      this.sliders?.clear();
    }

    select(index, { focus = false } = {}) {
      const panel = this.panels[index];
      const tab = this.tabs[index];
      if (!panel || tab?.getAttribute('aria-selected') === 'true') {
        if (focus) tab?.focus();
        return;
      }

      this.tabs.forEach((item, tabIndex) => {
        const selected = tabIndex === index;
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
        item.tabIndex = selected ? 0 : -1;
      });

      this.panels.forEach((item, panelIndex) => {
        item.hidden = panelIndex !== index;
      });

      this.moveIndicator();

      // Panel was 0-width while hidden: reset to first slide and recalculate controls
      this.sliders.get(panel)?.reset();
      this.scrollToPanelTop(panel);

      if (tab) {
        this.scrollTabIntoView(tab);
        if (focus) tab.focus();
      }
    }

    scrollTabIntoView(tab) {
      if (!this.scroller || this.scroller.scrollWidth <= this.scroller.clientWidth) return;
      const left = tab.offsetLeft - (this.scroller.clientWidth - tab.offsetWidth) / 2;
      this.scroller.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    onTabClick(event) {
      const tab = event.target.closest('[role="tab"]');
      if (!tab) return;
      clearTimeout(this.hoverTimer);
      this.select(this.tabs.indexOf(tab));
    }

    onTabPointerOver(event) {
      if (event.pointerType !== 'mouse' || !canHover.matches) return;
      const tab = event.target.closest('[role="tab"]');
      if (!tab) return;
      clearTimeout(this.hoverTimer);
      this.hoverTimer = setTimeout(() => this.select(this.tabs.indexOf(tab)), HOVER_DELAY);
    }

    onTabPointerLeave() {
      clearTimeout(this.hoverTimer);
    }

    onTabKeydown(event) {
      const current = this.tabs.indexOf(document.activeElement);
      if (current === -1) return;

      const last = this.tabs.length - 1;
      let next;
      switch (event.key) {
        case 'ArrowRight':
          next = current === last ? 0 : current + 1;
          break;
        case 'ArrowLeft':
          next = current === 0 ? last : current - 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.select(next, { focus: true });
    }

    // Selecting a tab or one of its cards in the editor opens that tab
    onBlockSelect(event) {
      const panel = event.target.closest('.tcl__panel');
      const index = this.panels.indexOf(panel);
      if (index > -1) this.select(index);
    }

    onSectionUnload(event) {
      if (event.detail.sectionId === this.dataset.sectionId) this.destroy();
    }
  }

  customElements.define('tabbed-collection-lineup', TabbedCollectionLineup);
}
